import { getLocalTimeZone } from "@internationalized/date";
import { startOfDay } from "date-fns";
import { DashedLinesGap } from "features/CenterContent/RoleContent/ProductionDeviation/components/DashedLinesGap";
import { SelectTrainIdsContent } from "features/CenterContent/RoleContent/ProductionDeviation/steps/SelectTrainIdsContent";
import { ProductionDeviationForm } from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { useTrainListRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainListRange";
import {
  groupTrainsBySeries,
  makeBatchProps,
  PANEL_STYLE,
  useProductionDeviationPath,
} from "features/CenterContent/RoleContent/ProductionDeviation/utils";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { BatchPreview } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/batchSections/BatchPreview";
import { SubTypeInput } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/batchSections/SubTypeInput";
import { TrainRouteDiscrepancyWarning } from "features/VehicleSidebar/AffectedTrains/TrainRouteDiscrepancyWarning";
import { TrainRouteDiscrepancies } from "features/VehicleSidebar/AffectedTrains/types";
import { FC, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import {
  EmptySegmentedPanel,
  SegmentedPanelMatrix,
} from "shared/components/SegmentedPanelMatrix";
import { Box } from "shared/components/Spor";
import { sort } from "shared/utils/arrayUtils";

type SelectTrainIdsProps = {
  opInfoData: OperationalInformationTypes;
  trainSeries: string[];
  index: number;
  mode: "create" | "edit";
};

export const SelectTrainIds: FC<SelectTrainIdsProps> = ({
  trainSeries,
  index,
  opInfoData,
  mode,
}) => {
  const { t } = useTranslation();
  const trainSerie = trainSeries[index];

  const { watch } = useFormContext<ProductionDeviationForm>();

  const { path } = useProductionDeviationPath(trainSerie);

  const durations = watch("durations");
  const fromTime = startOfDay(durations.from_time.toDate(getLocalTimeZone()));
  const toTime = startOfDay(durations.to_time.toDate(getLocalTimeZone()));

  const selectedTrains = watch(path("selectedTrains"));

  /**
   * We use all trainSeries since it doesnt cost anything more for the backend,
   * trainSeries is solely used as a filter in the backend.
   */
  const { data, status } = useTrainListRange(fromTime, toTime, trainSeries);

  const [routeDiscrepancies, setRouteDiscrepancies] =
    useState<TrainRouteDiscrepancies>(null);

  const showBatch = selectedTrains.length > 0;
  const batchProps = makeBatchProps(path, opInfoData, mode);

  const getTrains = () => {
    if (!data) return [];
    const trainsBySeries = groupTrainsBySeries(data.trainList);
    const trainsThisSerie = trainsBySeries[trainSerie];
    return trainsThisSerie ?? [];
  };

  return (
    <SegmentedPanelMatrix
      panelStyle={PANEL_STYLE}
      variant="secondary"
      noDashedLineGaps
    >
      {/* Pack into one child */}
      <>
        {status === "pending" && <SkeletonLoader skeletonType="selectTrains" />}
        {status === "error" && <FailureMessage flex={1} m="0px" maxW="100%" />}
        {status === "success" && (
          <SelectTrainIdsContent
            trainSerie={trainSerie}
            trainList={sort(getTrains(), (train) => train.trainNumber)}
            setRouteDiscrepancies={setRouteDiscrepancies}
          />
        )}
      </>
      <DashedLinesGap variant="secondary">
        {routeDiscrepancies?.[trainSerie] && (
          <Box
            // This box guards `TrainRouteDiscrepancyWarning` from css targetting
            p="0px !important"
          >
            <TrainRouteDiscrepancyWarning
              trainRouteDiscrepancies={routeDiscrepancies}
              title={t(
                "Noen valgte tog kjører ulike traseer. Sjekk at du velger riktige tog til behandling.",
              )}
            />
          </Box>
        )}
        {showBatch && <SubTypeInput {...batchProps} />}
        {showBatch && <BatchPreview {...batchProps} />}

        <EmptySegmentedPanel />
      </DashedLinesGap>
    </SegmentedPanelMatrix>
  );
};
